import { SyntheticEvent } from 'react';
import { PersonHelpers, PersonsV3 } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import { removeNonDigits } from '@frontend/phone-numbers';
import { createModalEvent, createPlaceCallEvent, useQueueStore } from '@frontend/queue';
import { useAppScopeStore } from '@frontend/scope';
import { CallSelectionContext } from './types';

const queryKeys = {
  base: ['calls'],
  outboundNumbers: (tenantId: string) => [...queryKeys.base, 'outboundVoiceNumbers', tenantId],
};

type UsePhoneCallActionArgs = {
  context: CallSelectionContext;
};

type UsePhoneCallActionReturnTypes = {
  handleCallActionClick: (e?: SyntheticEvent, overrideNumber?: string) => void;
  disabled: boolean;
  disabledDetails: string;
  multipleContactNumbers: ReturnType<typeof PersonHelpers.getAllPhoneNumbers>;
};

// Case 1: if personNumber is passed in the context, that means we don't need to give options to select from contact numbers
// Case 2: if person data is passed from person profile or contact page, then provide options to select from various contact numbers
// Case 3: if person Id is passed, then fetch the personData and provide options to select from various contact numbers
// Also, if outbound numbers are multiple then show the modal where we provide options to select the outbound numbers

export const usePhoneCallAction = ({ context }: UsePhoneCallActionArgs): UsePhoneCallActionReturnTypes => {
  const { t } = useTranslation('phone-call');
  const { phoneConfig } = usePhoneConfigShallowStore('phoneConfig');
  const { selectedLocationIds } = useAppScopeStore();

  let disabledDetails = '';

  const { data: personData, isLoading: isPersonDataLoading } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      personId: context?.personId ?? '',
      locationIds: selectedLocationIds,
    },
    {
      enabled: !context?.person && !!context?.personId,
      select: (data) => PersonsV3.PersonHelpers.convertPersonV3ToPerson(data),
    }
  );

  const person = context.person || personData;

  const multipleContactNumbers = PersonHelpers.getAllPhoneNumbers(person) ?? [];

  const dialNumber = removeNonDigits(context.phoneNumber || multipleContactNumbers[0]?.number);

  // dialNumber should atleast be one number
  // whether it gets returned from person or context.phoneNumber.
  const disabled = !dialNumber;

  const { triggerDialpad } = useDialpadTrigger();
  const { triggerClickToCallModal } = useClickToCallModalTrigger();

  const handleCallActionClick = (e?: SyntheticEvent, overrideNumber?: string) => {
    console.log('handleCallActionClick', { phoneConfig, dialNumber, overrideNumber });
    const numToDial = overrideNumber ?? dialNumber;
    if (!numToDial) {
      console.warn('No phone number available to dial');
      return;
    }
    e?.stopPropagation();
    if (!!phoneConfig) {
      triggerDialpad(numToDial, context.outboundNumber);
    } else {
      triggerClickToCallModal({
        dialNumber: numToDial,
        isLoading: isPersonDataLoading,
        outboundNumber: context.outboundNumber,
      });
    }
  };

  if (disabled) {
    disabledDetails = t('This contact has no associated phone numbers.');
  }

  return {
    handleCallActionClick,
    multipleContactNumbers,
    disabled: !!disabledDetails,
    disabledDetails: disabledDetails,
  };
};

export const useClickToCallModalTrigger = () => {
  const { enqueue } = useQueueStore.getState();

  const triggerModal = ({
    dialNumber,
    isLoading,
    outboundNumber,
  }: {
    dialNumber: string;
    isLoading: boolean;
    outboundNumber?: string;
  }) => {
    const event = createModalEvent({
      dialNumber,
      isLoading,
      outboundNumber: outboundNumber || '',
    });

    enqueue('clickToCallModal', {
      ...event,
      waitForHandler: true,
    });
  };

  return { triggerClickToCallModal: triggerModal };
};

export const useDialpadTrigger = () => {
  const { enqueue } = useQueueStore.getState();

  const triggerDialpad = (dialNumber: string, specificOutboundNumber?: string) => {
    // Use the event creator from the queue module
    const event = createPlaceCallEvent({
      dialNumber,
      outboundNumber: specificOutboundNumber,
    });

    enqueue('dialpad', {
      ...event,
      waitForHandler: true,
    });
  };

  return { triggerDialpad };
};
